import {
  FC,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Check as CheckIcon } from "@material-ui/icons";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { Alert } from "@material-ui/lab";
import debounce from "lodash/debounce";

import { Button, TypingIndicator } from "components";
import { EMPTY_VALUE_PLACEHOLDER } from "constants/index";
import { formatAmount, formatPercentage } from "lib/format";
import { parseMoney } from "lib/parse";
import { DEFAULT_ERROR_MESSAGE, longDebounceTime } from "services/constants";
import {
  createListingCustomCashflow,
  updateListingCustomCashflow,
  useListingCustomCashflows,
} from "services/listingCustomCashflows";
import { theme } from "theme";

import CashflowTable from "./CashflowTable";
import IrrMoneyInput from "./IrrMoneyInput";

const calculateTotal = (tenYearCashflows: string[], exitPrice: string) => {
  const cashflowTotal = tenYearCashflows.reduce((sum, cashflow) => {
    try {
      return sum + parseMoney(cashflow);
    } catch {
      return sum;
    }
  }, 0);
  const exitPriceValue = parseMoney(exitPrice);

  const total = cashflowTotal + exitPriceValue;
  return total > 0 ? formatAmount(total, 0) : EMPTY_VALUE_PLACEHOLDER;
};

const useStyles = makeStyles(theme => ({
  sectionTitle: {
    fontSize: "1.5rem",
    fontWeight: theme.typography.fontWeightBold,
    paddingRight: theme.spacing(0.5),
    textTransform: "none",
    textAlign: "left",
  },
  infoIcon: {
    fontSize: "1rem",
    color: theme.palette.text.secondary,
    cursor: "help",
    marginLeft: theme.spacing(0.5),
  },
  accordion: {
    backgroundColor: theme.custom.palette.gray.light,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(2),
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: `0 0 ${theme.spacing(2)}px 0 !important`,
    },
  },
  accordionSummary: {
    cursor: "default",
    pointerEvents: "none",
    padding: theme.spacing(2),
    minHeight: "unset !important",
    "&.Mui-expanded": {
      minHeight: "unset !important",
    },
    "& .MuiAccordionSummary-content": {
      alignItems: "center",
      margin: "0 !important",
    },
  },
  accordionDetails: {
    flexDirection: "column",
    padding: theme.spacing(0, 2, 2, 2),
    margin: "0 !important",
  },
  expandIcon: {
    marginLeft: theme.spacing(0.5),
    fontSize: "1rem",
    transition: "transform 0.3s ease-in-out",
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
    },
  },
  expandIconRotated: {
    transform: "rotate(180deg)",
  },
  expandButton: {
    textTransform: "none",
    fontSize: "0.75rem",
    fontWeight: theme.typography.fontWeightMedium,
    padding: theme.spacing(0.5, 1),
    minWidth: "auto",
    marginLeft: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      marginLeft: 0,
      padding: 0,
      minWidth: "unset",
    },
  },
  summaryContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "space-between",
    margin: 0,
  },
  summaryGrid: {
    flex: 1,
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
  },
  irrDisplay: {
    fontSize: "1rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(0.25),
    color: theme.palette.text.primary,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
    },
  },
  irrSubtitle: {
    textAlign: "center",
    marginBottom: 0,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  tableWrapper: {
    overflow: "visible",
  },
  tableGrid: {
    "& .MuiGrid-item": {
      paddingTop: 0,
      paddingBottom: 0,
    },
  },
  iconContainer: {
    display: "flex",
    alignItems: "center",
  },
  savedCheckIcon: {
    fontSize: "0.8rem",
    color: theme.palette.success.main,
    verticalAlign: "middle",
  },
  savedCheckText: {
    fontSize: "0.9em",
    color: theme.palette.success.main,
  },
  purchasePriceGrid: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  purchasePriceItem: {
    [theme.breakpoints.down("xs")]: {
      order: 1,
    },
  },
  irrItem: {
    [theme.breakpoints.down("xs")]: {
      order: 3,
    },
  },
  totalItem: {
    [theme.breakpoints.down("xs")]: {
      order: 2,
    },
  },
  openButton: {
    pointerEvents: "auto",
    [theme.breakpoints.down("xs")]: {
      order: 4,
    },
  },
  helperText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    textAlign: "left",
  },
  actionButtons: {
    gap: theme.spacing(1),
  },
}));

interface IRRCalculatorProps {
  listingId: number;
}

const IRRCalculator: FC<IRRCalculatorProps> = ({ listingId }) => {
  const classes = useStyles();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  const {
    data: listingCustomCashflows,
    isLoading,
    refetch,
  } = useListingCustomCashflows(listingId);
  const listingCustomCashflow = listingCustomCashflows?.[0];

  const [errorMessage, setErrorMessage] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const [exitPrice, setExitPrice] = useState<string>("0.00");
  const [irrPercentage, setIrrPercentage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchasePrice, setPurchasePrice] = useState<string>("0.00");
  const [tenYearCashflows, setTenYearCashflows] = useState<string[]>(
    Array(10).fill("0.00"),
  );

  // Using refs to avoid recreating the debounced submit handler on every render.
  const exitPriceRef = useRef(exitPrice);
  const listingCustomCashflowRef = useRef(listingCustomCashflow);
  const purchasePriceRef = useRef(purchasePrice);
  const tenYearCashflowsRef = useRef(tenYearCashflows);
  // Generation counter - increments on every user change. Used to detect if new changes
  // occurred during an API call, even when multiple API calls are in-flight concurrently.
  const changeGenerationRef = useRef(0);
  // Track if the form has unsaved local changes - prevents server data from overwriting user input
  const isDirtyRef = useRef(false);

  useEffect(() => {
    exitPriceRef.current = exitPrice;
    listingCustomCashflowRef.current = listingCustomCashflow;
    purchasePriceRef.current = purchasePrice;
    tenYearCashflowsRef.current = tenYearCashflows;
  }, [tenYearCashflows, purchasePrice, exitPrice, listingCustomCashflow]);

  useEffect(() => {
    // Only sync from server when the form is clean (no pending user changes).
    // This prevents stale server data from overwriting user input during rapid edits.
    if (isDirtyRef.current) {
      return;
    }

    const cashflowData =
      listingCustomCashflow?.ten_year_cashflows || Array(10).fill(0);
    setTenYearCashflows(
      cashflowData.map(cashflow => Number(cashflow).toFixed(2)),
    );
    setPurchasePrice(
      listingCustomCashflow?.purchase_price
        ? listingCustomCashflow.purchase_price
        : "0.00",
    );
    setIrrPercentage(listingCustomCashflow?.irr_percentage ?? null);
    setExitPrice(
      listingCustomCashflow?.exit_price
        ? listingCustomCashflow.exit_price
        : "0.00",
    );
  }, [listingCustomCashflow]);

  const displayIRR = useMemo(() => {
    if (isSubmitting) {
      return <TypingIndicator />;
    }
    return irrPercentage
      ? formatPercentage(Number(irrPercentage) / 100)
      : EMPTY_VALUE_PLACEHOLDER;
  }, [irrPercentage, isSubmitting]);

  const handleSubmit = useCallback(async () => {
    // Capture the current generation at the start of this submit.
    // If it changes during the API call, we know new edits came in.
    const generationAtStart = changeGenerationRef.current;

    const currentCashflows = tenYearCashflowsRef.current;
    const currentPurchasePrice = purchasePriceRef.current;
    const currentExitPrice = exitPriceRef.current;
    const currentListingCustomCashflow = listingCustomCashflowRef.current;

    setErrorMessage("");

    try {
      const payload = {
        listing: listingId,
        ten_year_cashflows: currentCashflows.map(cashflow =>
          parseMoney(cashflow || "0").toFixed(2),
        ),
        purchase_price:
          parseMoney(currentPurchasePrice) > 0
            ? parseMoney(currentPurchasePrice).toFixed(2)
            : null,
        exit_price:
          parseMoney(currentExitPrice) > 0
            ? parseMoney(currentExitPrice).toFixed(2)
            : null,
      };

      const result = !!currentListingCustomCashflow
        ? await updateListingCustomCashflow(
            currentListingCustomCashflow.id,
            payload,
          )
        : await createListingCustomCashflow(payload);
      // optimistically update the ref data
      listingCustomCashflowRef.current = result;

      if (result?.irr_percentage) {
        setIrrPercentage(result.irr_percentage);
      }

      // Only mark as clean if no new changes came in during this API call.
      // Compare the generation we captured at start vs current generation.
      const generationNow = changeGenerationRef.current;
      const hasNewChanges = generationNow !== generationAtStart;

      if (!hasNewChanges) {
        isDirtyRef.current = false;
        refetch();
        setShowSaveConfirmation(true);
      }

      return result;
    } catch (e) {
      handleError(e);
    } finally {
      setIsSubmitting(false);
    }
  }, [listingId, refetch]);

  const debouncedHandleSubmit = useMemo(
    () => debounce(handleSubmit, longDebounceTime),
    [handleSubmit],
  );

  useEffect(() => {
    return () => {
      debouncedHandleSubmit.cancel();
    };
  }, [debouncedHandleSubmit]);

  const handleCashflowChange = (index: number, value: string) => {
    changeGenerationRef.current += 1;
    isDirtyRef.current = true;

    const updatedCashflows = [...tenYearCashflows];
    updatedCashflows[index] = value;
    setTenYearCashflows(updatedCashflows);
    setIsSubmitting(true);
    setShowSaveConfirmation(false);
    debouncedHandleSubmit();
  };

  const handleExitPriceChange = (value: string) => {
    changeGenerationRef.current += 1;
    isDirtyRef.current = true;

    setExitPrice(value);
    setIsSubmitting(true);
    setShowSaveConfirmation(false);
    debouncedHandleSubmit();
  };

  const handlePurchasePriceChange = (value: string) => {
    changeGenerationRef.current += 1;
    isDirtyRef.current = true;

    setPurchasePrice(value);
    setIsSubmitting(true);
    setShowSaveConfirmation(false);
    debouncedHandleSubmit();
  };

  const handleClear = async () => {
    setErrorMessage("");
    setIsClearing(true);
    try {
      if (listingCustomCashflow?.id) {
        await updateListingCustomCashflow(listingCustomCashflow.id, {
          listing: listingId,
          ten_year_cashflows: Array(10).fill("0.00"),
          purchase_price: null,
          exit_price: null,
        });

        setTenYearCashflows(Array(10).fill("0.00"));
        setPurchasePrice("0.00");
        setIrrPercentage(null);
        setExitPrice("0.00");

        isDirtyRef.current = false;
        setIsSubmitting(false);
        setIsClearing(false);
        return;
      }
    } catch (e) {
      handleError(e);
      setIsClearing(false);
      return;
    }
  };

  const handleError = (e: unknown) => {
    const errorMessage = e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE;
    setErrorMessage(errorMessage);
  };

  useEffect(() => {
    if (!showSaveConfirmation) {
      return;
    }

    const hideConfirmationTimeout = setTimeout(() => {
      setShowSaveConfirmation(false);
    }, 3000);

    return () => clearTimeout(hideConfirmationTimeout);
  }, [showSaveConfirmation]);

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <>
          <Box display="flex" alignItems="flex-start" mb={1}>
            <Typography variant="h3" className={classes.sectionTitle}>
              IRR Calculator
            </Typography>
          </Box>
          <Typography className={classes.helperText}>
            Add your cashflows below to store your personalized IRR for this
            listing. They will never be shared with sellers or other buyers.
          </Typography>
          <Accordion className={classes.accordion} expanded={expanded}>
            <AccordionSummary className={classes.accordionSummary}>
              <Box className={classes.summaryContent}>
                <Grid container spacing={4} className={classes.summaryGrid}>
                  <Grid
                    item
                    xs={6}
                    sm={4}
                    md={3}
                    className={classes.purchasePriceItem}
                  >
                    <Box className={classes.summaryItem}>
                      <Typography
                        variant="caption"
                        className={classes.irrSubtitle}
                      >
                        Purchase Price
                      </Typography>
                    </Box>
                    <Typography className={classes.irrDisplay}>
                      {parseMoney(purchasePrice) > 0
                        ? formatAmount(parseMoney(purchasePrice), 0)
                        : EMPTY_VALUE_PLACEHOLDER}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4} md={3} className={classes.totalItem}>
                    <Box className={classes.summaryItem} alignItems="center">
                      <Typography
                        variant="caption"
                        className={classes.irrSubtitle}
                      >
                        Total Cashflow
                      </Typography>
                      <Typography className={classes.irrDisplay}>
                        {calculateTotal(tenYearCashflows, exitPrice)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4} md={3} className={classes.irrItem}>
                    <Box className={classes.summaryItem}>
                      <Typography
                        variant="caption"
                        className={classes.irrSubtitle}
                      >
                        IRR
                      </Typography>
                      <Typography className={classes.irrDisplay}>
                        {displayIRR}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={3}
                    className={classes.openButton}
                  >
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      alignItems="center"
                      height="100%"
                    >
                      <Button
                        style={{ minWidth: "135px" }}
                        size={isMdUp ? "small" : "medium"}
                        fullWidth={!isMdUp}
                        variant="contained"
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          setExpanded(!expanded);
                        }}
                        customColor="purple"
                      >
                        {expanded ? "Hide cashflows" : "View cashflows"}
                        <ExpandMoreIcon
                          className={`${classes.expandIcon} ${
                            expanded ? classes.expandIconRotated : ""
                          }`}
                        />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              {!!errorMessage && (
                <Box mt={2} mb={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </Box>
              )}
              <Box className={classes.tableWrapper}>
                <Grid
                  container
                  spacing={2}
                  className={classes.purchasePriceGrid}
                >
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box pl={1}>
                        <Typography variant="body2">
                          <strong>Purchase Price</strong>
                        </Typography>
                      </Box>
                      <Box mr={2}>
                        <IrrMoneyInput
                          name="purchase_price"
                          value={purchasePrice}
                          onChange={handlePurchasePriceChange}
                        />
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} className={classes.tableGrid}>
                  <Grid item xs={12} md={6}>
                    <CashflowTable
                      cashflows={tenYearCashflows.slice(0, 5)}
                      startYear={1}
                      showHeader
                      onCashflowChange={handleCashflowChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <CashflowTable
                      cashflows={tenYearCashflows.slice(5, 10)}
                      startYear={6}
                      showHeader={isMdUp}
                      onCashflowChange={handleCashflowChange}
                      showExitPrice
                      exitPrice={exitPrice}
                      onExitPriceChange={handleExitPriceChange}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mt={2}
                className={classes.actionButtons}
              >
                {showSaveConfirmation && (
                  <Box className={classes.iconContainer} mr={2}>
                    <CheckIcon className={classes.savedCheckIcon} />
                    <Typography
                      variant="body2"
                      className={classes.savedCheckText}
                    >
                      Saved
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  customColor="gray"
                  onClick={handleClear}
                  disabled={isClearing}
                >
                  Clear
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}
    </>
  );
};

export default IRRCalculator;
